import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TextField, AreaField, SelectField, EditorDialog, EmptyState, Spinner } from "./AdminShared";

const EMPTY = { title: "", type: "Workshop", mode: "In-Person", date: "", time: "", location: "", description: "", capacity: "" };

export const EventsManager = () => {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/events").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load events"));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    const body = { ...editing, capacity: editing.capacity === "" || editing.capacity == null ? null : Number(editing.capacity) };
    delete body.id; delete body.created_at;
    try {
      if (editing.id) await api.put(`/events/${editing.id}`, body); else await api.post("/events", body);
      toast.success(editing.id ? "Event updated" : "Event created");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    await api.delete(`/events/${ev.id}`); toast.success("Event deleted"); load();
  };

  const set = (k) => (v) => setEditing((e) => ({ ...e, [k]: v }));

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="events-manager">
      <div className="p-4 border-b border-slate-100 flex justify-end">
        <Button onClick={() => setEditing({ ...EMPTY })} className="rounded-full bg-gold hover:bg-gold-light text-white" data-testid="event-add-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Event
        </Button>
      </div>
      {!rows ? <Spinner /> : rows.length === 0 ? <EmptyState text="No events yet." /> : (
        <div className="divide-y divide-slate-100">
          {rows.map((ev) => (
            <div key={ev.id} className="p-4 flex items-start gap-4 hover:bg-slate-50" data-testid={`event-row-${ev.id}`}>
              <div className="h-10 w-10 rounded-xl bg-navy-900 text-gold-light flex items-center justify-center shrink-0"><Calendar className="h-5 w-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-navy-900 truncate">{ev.title}</span>
                  <Badge variant="outline" className="text-[10px]">{ev.type}</Badge>
                  <Badge variant="outline" className="text-[10px]">{ev.mode}</Badge>
                </div>
                <p className="text-sm text-slate-500 truncate">{ev.date} · {ev.time} · {ev.location}</p>
              </div>
              <Button size="icon" variant="ghost" onClick={() => setEditing({ ...EMPTY, ...ev, capacity: ev.capacity ?? "" })} data-testid={`event-edit-${ev.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="text-red-600" onClick={() => remove(ev)} data-testid={`event-delete-${ev.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EditorDialog open onClose={() => setEditing(null)} title={editing.id ? "Edit Event" : "New Event"} onSave={save} saving={saving} testid="event-editor">
          <TextField label="Title" value={editing.title} onChange={set("title")} testid="event-title-input" required />
          <div className="grid sm:grid-cols-2 gap-4">
            <SelectField label="Type" value={editing.type} onChange={set("type")} testid="event-type-select"
              options={["Workshop", "Webinar", "Community Event", "Mentorship", "Fundraiser"].map((v) => ({ value: v, label: v }))} />
            <SelectField label="Mode" value={editing.mode} onChange={set("mode")} testid="event-mode-select"
              options={["In-Person", "Virtual", "Hybrid"].map((v) => ({ value: v, label: v }))} />
            <TextField label="Date" type="date" value={editing.date} onChange={set("date")} testid="event-date-input" required />
            <TextField label="Time" value={editing.time} onChange={set("time")} testid="event-time-input" />
            <TextField label="Location" value={editing.location} onChange={set("location")} testid="event-location-input" />
            <TextField label="Capacity" type="number" value={editing.capacity} onChange={set("capacity")} testid="event-capacity-input" />
          </div>
          <AreaField label="Description" value={editing.description} onChange={set("description")} testid="event-description-input" />
        </EditorDialog>
      )}
    </div>
  );
};
